import { useProfile } from '@shared/hooks/useProfile';
import { useSound } from '@shared/hooks/useSound';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useRef } from 'react';
import notificationSound from '../../../assets/sounds/notification.mp3';
import { db } from '../../../config/firebase';
import { useChitChat } from '../context/ChitChatContext';

export const useChatNotificationSound = () => {
  const { user } = useProfile();
  const { chatWindows } = useChitChat();
  const { play: playSound } = useSound(notificationSound, { volume: 0.3 });
  const lastMessageTimestamps = useRef<Record<string, number>>({});
  const isInitialLoad = useRef<Record<string, boolean>>({});
  const messageUnsubscribersRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    if (!user?.id) return;

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.id)
    );

    const unsubscribeConversations = onSnapshot(conversationsQuery, (conversationsSnapshot) => {
      const currentConversationIds = new Set(conversationsSnapshot.docs.map(doc => doc.id));

      Object.keys(messageUnsubscribersRef.current).forEach(convId => {
        if (!currentConversationIds.has(convId)) {
          messageUnsubscribersRef.current[convId]();
          delete messageUnsubscribersRef.current[convId];
          delete lastMessageTimestamps.current[convId];
          delete isInitialLoad.current[convId];
        }
      });

      conversationsSnapshot.docs.forEach(conversationDoc => {
        const conversationId = conversationDoc.id;

        if (messageUnsubscribersRef.current[conversationId]) return;

        isInitialLoad.current[conversationId] = true;

        const messagesQuery = query(
          collection(db, 'conversations', conversationId, 'messages'),
          orderBy('timestamp', 'desc'),
          limit(1)
        );

        const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
          if (snapshot.empty) return;

          const latestDoc = snapshot.docs[0];
          const data = latestDoc.data();
          const messageTimestamp = data.timestamp?.toDate?.()?.getTime() || 0;
          const senderId = data.senderId;

          if (isInitialLoad.current[conversationId]) {
            lastMessageTimestamps.current[conversationId] = messageTimestamp;
            isInitialLoad.current[conversationId] = false;
            return;
          }

          const lastTimestamp = lastMessageTimestamps.current[conversationId] || 0;

          if (messageTimestamp > lastTimestamp && senderId !== user.id) {
            const chatWindow = chatWindows.find((w) => w.conversationId === conversationId);
            const isWindowOpen = chatWindow && !chatWindow.isMinimized;

            if (!isWindowOpen) {
              playSound();
            }

            lastMessageTimestamps.current[conversationId] = messageTimestamp;
          }
        });

        messageUnsubscribersRef.current[conversationId] = unsubscribe;
      });
    });

    return () => {
      unsubscribeConversations();
      Object.values(messageUnsubscribersRef.current).forEach(unsub => unsub());
      messageUnsubscribersRef.current = {};
    };
  }, [user?.id, chatWindows, playSound]);
};
